import { JoinCodeSchema } from "@saboteur/contracts";

const TOKEN_KEY_PREFIX = "saboteur:participant-token:";
export const RECOVERY_FRAGMENT_KEY = "recovery";

function storageKey(joinCode: string): string {
  return `${TOKEN_KEY_PREFIX}${JoinCodeSchema.parse(joinCode)}`;
}

function browserStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

export function storeParticipantToken(joinCode: string, participantToken: string): void {
  if (participantToken.length === 0) {
    throw new Error("Participant token cannot be empty");
  }

  browserStorage()?.setItem(storageKey(joinCode), participantToken);
}

export function getParticipantToken(joinCode: string): string | undefined {
  return browserStorage()?.getItem(storageKey(joinCode)) ?? undefined;
}

export function removeParticipantToken(joinCode: string): void {
  browserStorage()?.removeItem(storageKey(joinCode));
}

/** Builds a same-origin link whose secret stays in the URL fragment and is never sent to the server. */
export function createPlayerRecoveryUrl(
  joinCode: string,
  participantToken: string,
  origin = typeof window === "undefined" ? "http://localhost" : window.location.origin,
): string {
  const code = JoinCodeSchema.parse(joinCode);
  const url = new URL(`/room/${code}`, origin);
  url.hash = new URLSearchParams({ [RECOVERY_FRAGMENT_KEY]: participantToken }).toString();
  return url.toString();
}

export function readRecoveryToken(
  fragment = typeof window === "undefined" ? "" : window.location.hash,
): string | undefined {
  const rawFragment = fragment.startsWith("#") ? fragment.slice(1) : fragment;
  const token = new URLSearchParams(rawFragment).get(RECOVERY_FRAGMENT_KEY)?.trim();
  return token ? token : undefined;
}

/** Imports a fragment token, then removes it from browser history to reduce accidental sharing. */
export function importRecoveryToken(joinCode: string): string | undefined {
  const token = readRecoveryToken();
  if (!token) return getParticipantToken(joinCode);

  storeParticipantToken(joinCode, token);
  if (typeof window !== "undefined") {
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${window.location.search}`,
    );
  }
  return token;
}
